/**
 * Keyboard focus for a modal window: focus moves into it when it opens (unless
 * a field inside already took it, e.g. `autoFocus`), Tab and Shift+Tab cycle
 * inside it, and focus returns to what had it when the window closes.
 *
 * Only the window in FRONT keeps the keyboard: a confirmation can open over a
 * form (ours or antd's), and Tab / Escape there belong to the confirmation.
 */
import { useEffect, useRef, type RefObject } from 'react';

// Every kind of window the app opens, all portalled to <body>: the last one
// shown in the document is the one in front. antd keeps a closed modal's wrap
// in the DOM, hidden, so only the visible ones count - and a declarative antd
// <Modal> reopened over one of ours keeps its first place in the document, so
// give such a modal `destroyOnHidden` (every one in use today has it, and
// `Modal.useModal()` confirmations are removed when they close).
const WINDOWS = '.cxd-overlay, .confirm-modal-overlay, .ant-modal-wrap';

export function isFrontWindow(overlay: Element | null): boolean {
  const shown = Array.from(document.querySelectorAll(WINDOWS)).filter((el) => el.getClientRects().length > 0);
  return !!overlay && shown[shown.length - 1] === overlay;
}

/** An antd popup (a select's list, a date picker, a ⋯ menu) is open: Escape closes it first. */
export function popupOpen(): boolean {
  return !!document.querySelector(
    '.ant-select-dropdown:not(.ant-select-dropdown-hidden), .ant-picker-dropdown:not(.ant-picker-dropdown-hidden), '
    + '.ant-dropdown:not(.ant-dropdown-hidden)');
}

/**
 * Escape closes the window in FRONT only: a confirmation opened over a form
 * answers Escape itself, and an open select list or date picker closes first.
 * `onEscape` decides what closing means (e.g. asking about unsaved changes).
 */
export function useFrontWindowEscape(overlayRef: RefObject<HTMLElement | null>, onEscape: () => void): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || popupOpen()) return;
      if (!isFrontWindow(overlayRef.current)) return;
      onEscape();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [overlayRef, onEscape]);
}

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
  'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter((el) => el.offsetParent !== null || el === document.activeElement);
}

export function useDialogFocus(dialogRef: RefObject<HTMLElement | null>,
                               overlayRef: RefObject<HTMLElement | null>): void {
  // What had focus when the window opened - read on the first render, before a
  // field's autoFocus (applied when the window is mounted) takes it.
  const openerRef = useRef<HTMLElement | null | undefined>(undefined);
  if (openerRef.current === undefined) {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    const opener = openerRef.current;
    // After the first paint, so a field's own autoFocus wins.
    const first = window.setTimeout(() => {
      if (!dialog.contains(document.activeElement)) (focusables(dialog)[0] || dialog).focus();
    }, 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !isFrontWindow(overlayRef.current)) return;
      const items = focusables(dialog);
      if (!items.length) { e.preventDefault(); return; }
      const head = items[0];
      const tail = items[items.length - 1];
      const inside = dialog.contains(document.activeElement);
      if (!inside) {
        e.preventDefault();
        (e.shiftKey ? tail : head).focus();
      } else if (e.shiftKey && document.activeElement === head) {
        e.preventDefault();
        tail.focus();
      } else if (!e.shiftKey && document.activeElement === tail) {
        e.preventDefault();
        head.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(first);
      document.removeEventListener('keydown', onKey);
      // Back to the control that opened the window, if it is still on the page.
      if (opener && opener.isConnected) opener.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per opening
  }, []);
}
